import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getImpersonado, clearImpersonado } from '../utils/impersonation';
import useAuth from '../hooks/useAuth';

/**
 * Aviso no topo do conteúdo quando o Master está impersonando outro usuário.
 * Sempre visível enquanto a impersonation está ativa — pra ninguém esquecer
 * que as ações estão saindo no nome do impersonado.
 *
 * Botão "Sair da impersonação" limpa o localStorage e volta pro /dashboard
 * (chooser do Master).
 */
export default function ImpersonationBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleGlobal } = useAuth();
  const imp = getImpersonado();
  // Só renderiza pra Master de verdade. Se outro usuário tiver localStorage
  // residual (raro — limpamos no signOut), o backend já ignora o header e
  // aqui também não exibe nada confuso.
  if (!imp || roleGlobal !== 'Master') return null;

  const handleSair = () => {
    clearImpersonado();
    navigate('/dashboard', { replace: true });
    // Hard reload garante que qualquer state em cache (bootstrap, etc.) limpe junto.
    // Sem isso, /dashboard ainda mostraria os dados do impersonado em tela.
    setTimeout(() => window.location.reload(), 0);
  };

  const tipoLabel = imp.tipo === 'jurado' ? t('as Judge') : imp.tipo === 'produtor' ? t('as Producer') : '';

  return (
    <Alert
      severity="warning"
      icon={<VisibilityIcon />}
      sx={{
        'mb': 3,
        'alignItems': 'center',
        'border': 1,
        'borderColor': 'warning.light',
        '& .MuiAlert-message': { flex: 1 },
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1}>
        <Box flex={1}>
          <Typography variant="body2" component="span" fontWeight={700} color="text.primary">
            {t('Impersonating')}: {imp.nome || `#${imp.usuarioId}`}
          </Typography>
          {tipoLabel && (
            <Typography variant="body2" component="span" sx={{ ml: 1 }}>
              {tipoLabel}
            </Typography>
          )}
        </Box>
        <Button size="small" variant="outlined" color="warning" startIcon={<LogoutIcon />} onClick={handleSair}>
          {t('Exit impersonation')}
        </Button>
      </Stack>
    </Alert>
  );
}
